import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { rolesForPath, landingForRole } from "./config/navigation";
import Layout from "./components/layout/Layout";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import RoomsPage from "./pages/RoomsPage";
import CalendarPage from "./pages/CalendarPage";
import UsersPage from "./pages/UsersPage";
import CommentsPage from "./pages/CommentsPage";
import VouchersPage from "./pages/VouchersPage";
import SiteSettingsPage from "./pages/SiteSettingsPage";
import PaymentSettingsPage from "./pages/PaymentSettingsPage";
import BookingsPage from "./pages/BookingsPage";
import EditHistoryPage from "./pages/EditHistoryPage";
import SpinPage from "./pages/SpinPage";
import GuideManagePage from "./pages/GuideManagePage";
import LLDTimeReviewPage from "./pages/LLDTimeReviewPage";
import SepayTransactionsPage from "./pages/SepayTransactionsPage";
import LldRevenuePage from "./pages/LldRevenuePage";
import WarehousePage from "./pages/WarehousePage";
import WarehouseDetailPage from "./pages/WarehouseDetailPage";
import NetflixMailPage from "./pages/NetflixMailPage";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

interface RouteProps {
  children: React.ReactNode;
}

function FullPageLoader(): React.ReactElement {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-coffee-50">
      <div className="h-9 w-9 rounded-full border-[3px] border-coffee-200 border-t-coffee-600 animate-spin" />
      <p className="text-sm text-zinc-500">Đang tải...</p>
    </div>
  );
}

// Redirect về trang phù hợp theo role
function RoleDefaultPage(): React.ReactElement {
  const { user } = useAuth();
  if (user?.role === "kiemkho") return <Navigate to="/calendar" replace />;
  return <DashboardPage />;
}

// Thanh bên chỉ ẩn menu; gõ thẳng đường dẫn vẫn vào được trang → chặn theo vai trò ở đây
// (backend vẫn là lớp bảo vệ chính, đây là để người dùng không thấy trang lỗi 403 lộn xộn)
function guard(path: string, element: React.ReactElement): React.ReactElement {
  const roles = rolesForPath(path);
  return <RequireRole roles={roles}>{element}</RequireRole>;
}

function RequireRole({ roles, children }: { roles: string[] | null; children: React.ReactNode }): React.ReactElement {
  const { user } = useAuth();
  if (roles && (!user || !roles.includes(user.role))) {
    return <Navigate to={landingForRole(user?.role)} replace />;
  }
  return <>{children}</>;
}

// Protected Route Component
function ProtectedRoute({ children }: RouteProps): React.ReactElement {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <FullPageLoader />;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Public Route Component (redirect if already logged in)
function PublicRoute({ children }: RouteProps): React.ReactElement {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) return <FullPageLoader />;

  if (isAuthenticated) {
    return <Navigate to={landingForRole(user?.role)} replace />;
  }

  return <>{children}</>;
}

function App(): React.ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public Route */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<RoleDefaultPage />} />
              <Route path="rooms" element={guard("rooms", <RoomsPage />)} />
              <Route path="calendar" element={guard("calendar", <CalendarPage />)} />
              <Route path="users" element={guard("users", <UsersPage />)} />
              <Route path="comments" element={guard("comments", <CommentsPage />)} />
              <Route path="bookings" element={guard("bookings", <BookingsPage />)} />
              <Route path="edit-history" element={guard("edit-history", <EditHistoryPage />)} />
              <Route path="vouchers" element={guard("vouchers", <VouchersPage />)} />
              <Route path="site-settings" element={guard("site-settings", <SiteSettingsPage />)} />
              <Route path="payment-settings" element={guard("payment-settings", <PaymentSettingsPage />)} />
              <Route path="spin" element={guard("spin", <SpinPage />)} />
              <Route path="guide" element={guard("guide", <GuideManagePage />)} />
              <Route path="lld-review" element={guard("lld-review", <LLDTimeReviewPage />)} />
              <Route path="lld-revenue" element={guard("lld-revenue", <LldRevenuePage />)} />
              <Route path="warehouse" element={guard("warehouse", <WarehousePage />)} />
              <Route path="warehouse/:id" element={guard("warehouse", <WarehouseDetailPage />)} />
              <Route path="netflix-mail" element={guard("netflix-mail", <NetflixMailPage />)} />
              <Route path="transactions" element={guard("transactions", <SepayTransactionsPage />)} />
              {/* Đường dẫn không tồn tại → về trang đầu thay vì trang trắng */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>

          <Toaster
            position="top-right"
            toastOptions={{
              duration: 3000,
              style: {
                background: "#363636",
                color: "#fff",
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: "#4ade80",
                  secondary: "#fff",
                },
              },
              error: {
                duration: 4000,
                iconTheme: {
                  primary: "#ef4444",
                  secondary: "#fff",
                },
              },
            }}
          />
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
